import * as $ from 'jquery';

/**
 * @var EventHooks
 * @author Kamil Bugajak
 * @version 1.0
 * @description Hooks to simplify applying events for components
 */

/**
 *  @method onLoad
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method on page load
 *
 *  @param callback Method to run on page load
 *  @see The page load in this context means, that this method will run when DOM finishes building.
 */
export function onLoad (callback): any {
    $('body').ready(callback);
}


/**
 *  @method onClick
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method on provided Selector click
 */
export function onClick(selector: string | Document | JQuery<HTMLElement>, callback: (T1, T2, T3, T4) => void): any {
    if (typeof selector !== 'string') {
        $(selector).on('click', callback);
        return;
    }
    $('body').on('click', selector, callback);
}

export function onClickOutsideOf(selector: string | JQuery<HTMLElement>, callback: () => void): any {
    $('body').on('mousedown', (event) => {
        let element = typeof selector === 'string' ? $(selector) : selector;
    
        if (
            !element.is(event.target) &&
            element.find(event.target).length === 0 &&
            // edge case - it still performing callback when you click on child input for some reason
            // This line prevents it
            !$(event.target).is('input')
        ) {
            callback();
        }
    });
}
/**
 *  @method onChange
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method on provided Selector change
 *
 *  @param selector
 *  @param callback
 *  @param stopPropagation If your method is called multiple times you can prevent it by setting this parameter to true. This parameter is optional so you do not have to pass it
 *  if you don't need to
 */
export function onChange(selector: string, callback: (T1, T2, T3, T4) => void, stopPropagation: boolean = false): any {
    if (stopPropagation) {
        $('body').on('change', selector, (e) => { e.stopPropagation(); callback; });
    } else {
        $('body').on('change', selector, callback);
    }

}

/**
 *  @method onBlur
 *  @memberOf EventHooks
 *  @deprecated
 *  @description Event Hook for Components which allows you to run provided method when focus is lost on provided Selector
 *  @see Do not use this EventHook! It supports Event Bubbling. I decided to keep it for backward compatibility. Use onFocusOut instead
 */
export function onBlur(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('blur', selector, callback);
    console.warn('onBlur hook detected. Please, do not use that because it my cause Event Bubbling. Use onFocusOut instead');
}

/**
 *  @method onDoubleClick
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method on provided Selector doubleclick
 */
export function onDoubleClick(selector: string| Document, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('dblclick', selector, callback);
}

/**
 *  @method onMouseEnter
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user will enter with his mouse pointer into your provided Selector
 */
export function onMouseEnter(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('mouseenter', selector, callback);
}

/**
 *  @method onMouseLeave
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user will leave his mouse pointer from your provided Selector
 */
export function onMouseLeave(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('mouseleave', selector, callback);
}

/**
 *  @method onMouseDown
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when the mouse pointer is over the element, and the mouse button is pressed.
 */
export function onMouseDown(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('mousedown', selector, callback);
}

/**
 *  @method onMouseUp
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when the mouse pointer is over the element, and the mouse button is released.
 */
export function onMouseUp(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('mouseup', selector, callback);

}

/**
 *  @method onHover
 *  @memberOf EventHooks
 *  @description Event Hook for Components which binds provided method for both mouseenter and mouseleave events
 */
export function onHover(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('hover', selector, callback);
}

/**
 *  @method onKeydown
 *  @memberOf EventHooks
 *  @description Event Hook for Components which fires provided method each time when user keeps button pressed and hold
 */
export function onKeyDown(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('keydown', selector, callback);
}

/**
 *  @method onKeyPress
 *  @memberOf EventHooks
 *  @description Event Hook for Components which fires provided method each time when user keeps button pressed but not hold
 *  @warning If you want to use it to call AJAX on input change, consider using onChange instead. onKeyPress in this situation
 * will fire AJAX with previous input value
 */
export function onKeyPress(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('keypress', selector, callback);
}

/**
 *  @method onFocusOut
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when focus is lost on provided Selector
 *  @see That's definitely better alternative for Blur event.
 */
export function onFocusOut(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('focusout', selector, callback);
}

/**
 *  @method onSubmit
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user submits the form
 *  @see That's definitely better alternative for Blur event.
 */
export function onSubmit(selector: string | JQuery<HTMLFormElement>, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('submit', selector, callback);
}

/**
 *  @method onResize
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user resizes browser window.
 */
export function onResize(callback: (T1, T2, T3, T4) => void): any {
    $('body').on('resize', callback);
}

/**
 *  @method on
 *  @memberOf EventHooks
 *  @description Hook for declaring custom events with optional data
 *  @see Do not use this hook if event that you're looking for is already declared. Also you need to keep in mind that Selector and callback are swaped in this hook, since selector parameter is optional and optional parameters has to be on the end of the parameters
 */
export function on(event: string, callback: (T1, T2, T3, T4) => void, selector?: string, data?: string | number | object): any {
    $('body').on(`${event}`, selector, callback);
}

/**
 *  @method onKeyup
 *  @memberOf EventHooks
 *  @description Event Hook for Components which fires provided method each time after user release keyboard button
 */
export function onKeyUp(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('keyup', selector, callback);
}

/**
 *  @method onResizeWindow
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user resizes browser window.
 */
 export function onResizeWindow(callback: (T1, T2, T3, T4) => void): any {
    $(window).on('resize', callback);
}
import * as $ from 'jquery';

/**
 * @var EventHooks
 * @author Kamil Bugajak
 * @version 1.0
 * @description Hooks to simplify applying events for components
 */

/**
 *  @method onLoad
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method on page load
 *
 *  @param callback Method to run on page load
 *  @see The page load in this context means, that this method will run when DOM finishes building.
 */
export function onLoad (callback): any {
    $('body').ready(callback);
}


/**
 *  @method onClick
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method on provided Selector click
 */
export function onClick(selector: string | Document | JQuery<HTMLElement>, callback: (T1, T2, T3, T4) => void): any {
    if (typeof selector !== 'string') {
        $(selector).on('click', callback);
        return;
    }
    $('body').on('click', selector, callback);
}

export function onClickOutsideOf(selector: string | JQuery<HTMLElement>, callback: () => void): any {
    $('body').on('mousedown', (event) => {
        let element = typeof selector === 'string' ? $(selector) : selector;
    
        if (
            !element.is(event.target) &&
            element.find(event.target).length === 0 &&
            // edge case - it still performing callback when you click on child input for some reason
            // This line prevents it
            !$(event.target).is('input')
        ) {
            callback();
        }
    });
}
/**
 *  @method onChange
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method on provided Selector change
 *
 *  @param selector
 *  @param callback
 *  @param stopPropagation If your method is called multiple times you can prevent it by setting this parameter to true. This parameter is optional so you do not have to pass it
 *  if you don't need to
 */
export function onChange(selector: string, callback: (T1, T2, T3, T4) => void, stopPropagation: boolean = false): any {
    if (stopPropagation) {
        $('body').on('change', selector, (e) => { e.stopPropagation(); callback; });
    } else {
        $('body').on('change', selector, callback);
    }

}

/**
 *  @method onBlur
 *  @memberOf EventHooks
 *  @deprecated
 *  @description Event Hook for Components which allows you to run provided method when focus is lost on provided Selector
 *  @see Do not use this EventHook! It supports Event Bubbling. I decided to keep it for backward compatibility. Use onFocusOut instead
 */
export function onBlur(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('blur', selector, callback);
    console.warn('onBlur hook detected. Please, do not use that because it my cause Event Bubbling. Use onFocusOut instead');
}

/**
 *  @method onDoubleClick
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method on provided Selector doubleclick
 */
export function onDoubleClick(selector: string| Document, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('dblclick', selector, callback);
}

/**
 *  @method onMouseEnter
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user will enter with his mouse pointer into your provided Selector
 */
export function onMouseEnter(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('mouseenter', selector, callback);
}

/**
 *  @method onMouseLeave
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user will leave his mouse pointer from your provided Selector
 */
export function onMouseLeave(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('mouseleave', selector, callback);
}

/**
 *  @method onMouseDown
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when the mouse pointer is over the element, and the mouse button is pressed.
 */
export function onMouseDown(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('mousedown', selector, callback);
}

/**
 *  @method onMouseUp
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when the mouse pointer is over the element, and the mouse button is released.
 */
export function onMouseUp(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('mouseup', selector, callback);

}

/**
 *  @method onHover
 *  @memberOf EventHooks
 *  @description Event Hook for Components which binds provided method for both mouseenter and mouseleave events
 */
export function onHover(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('hover', selector, callback);
}

/**
 *  @method onKeydown
 *  @memberOf EventHooks
 *  @description Event Hook for Components which fires provided method each time when user keeps button pressed and hold
 */
export function onKeyDown(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('keydown', selector, callback);
}

/**
 *  @method onKeyPress
 *  @memberOf EventHooks
 *  @description Event Hook for Components which fires provided method each time when user keeps button pressed but not hold
 *  @warning If you want to use it to call AJAX on input change, consider using onChange instead. onKeyPress in this situation
 * will fire AJAX with previous input value
 */
export function onKeyPress(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('keypress', selector, callback);
}

/**
 *  @method onFocusOut
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when focus is lost on provided Selector
 *  @see That's definitely better alternative for Blur event.
 */
export function onFocusOut(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('focusout', selector, callback);
}

/**
 *  @method onSubmit
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user submits the form
 *  @see That's definitely better alternative for Blur event.
 */
export function onSubmit(selector: string | JQuery<HTMLFormElement>, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('submit', selector, callback);
}

/**
 *  @method onResize
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user resizes browser window.
 */
export function onResize(callback: (T1, T2, T3, T4) => void): any {
    $('body').on('resize', callback);
}

/**
 *  @method on
 *  @memberOf EventHooks
 *  @description Hook for declaring custom events with optional data
 *  @see Do not use this hook if event that you're looking for is already declared. Also you need to keep in mind that Selector and callback are swaped in this hook, since selector parameter is optional and optional parameters has to be on the end of the parameters
 */
export function on(event: string, callback: (T1, T2, T3, T4) => void, selector?: string, data?: string | number | object): any {
    $('body').on(`${event}`, selector, callback);
}

/**
 *  @method onKeyup
 *  @memberOf EventHooks
 *  @description Event Hook for Components which fires provided method each time after user release keyboard button
 */
export function onKeyUp(selector: string, callback: (T1, T2, T3, T4) => void): any {
    $('body').on('keyup', selector, callback);
}

/**
 *  @method onResizeWindow
 *  @memberOf EventHooks
 *  @description Event Hook for Components which allows you to run provided method when user resizes browser window.
 */
 export function onResizeWindow(callback: (T1, T2, T3, T4) => void): any {
    $(window).on('resize', callback);
}
